/**
 * Full-page status / notification screen — a centered branded card with a
 * tone icon, title, message, and optional action buttons. Use it for
 * terminal states that aren't a form: "invitation not found", "wrong
 * account", an accept prompt, an error boundary, etc.
 */

import { type ReactNode } from 'react';

import { BliksysMark } from './BliksysMark.js';
import { Icon, type IconName } from './Icon.js';
import './StatusScreen.css';

export type StatusTone = 'info' | 'success' | 'error' | 'warning';

const TONE_ICON: Record<StatusTone, IconName> = {
  info: 'info-circle',
  success: 'check',
  error: 'alert-circle',
  warning: 'alert-circle',
};

interface Props {
  tone?: StatusTone;
  title: string;
  message?: ReactNode;
  /** Action buttons / links, rendered in a row under the message. */
  children?: ReactNode;
}

export function StatusScreen({ tone = 'info', title, message, children }: Props): JSX.Element {
  return (
    <div className="status-screen">
      <div className="status-card">
        <div className="status-brand" aria-hidden="true">
          <BliksysMark size={24} color="var(--accent-blue)" />
        </div>
        <div className={`status-badge status-badge-${tone}`} aria-hidden="true">
          <Icon name={TONE_ICON[tone]} size={26} strokeWidth={2.2} />
        </div>
        <h1 className="status-title">{title}</h1>
        {message != null && <p className="status-message">{message}</p>}
        {children != null && <div className="status-actions">{children}</div>}
      </div>
    </div>
  );
}
